import React, { useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';

import { Alert, AlertTitle, Box, Container, Typography } from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import {
  BlacklistAction,
  BulkSwitchGHAPropertiesAction,
  GHAAccountType,
  GHAPropertyBrand,
  GHAPropertyType,
  bulkAppendPricingRegions,
  bulkBlacklistProperties,
  bulkSwitchGHAProperties,
  bulkUpdateGHAPropertyType,
} from '~/services/ConnectionGoogleService';

import GHABulkOperationDialog from './GHABulkOperationDialog';
import GHABulkOperationsMenu from './GHABulkOperationsMenu';
import GHAPropertyListMetadata from './GHAPropertyListMetadata';
import GHAPropertySearch from './GHAPropertySearch';
import { getAccountTypeOptionsForBrand } from './utils';

type DialogType = 'switch-properties' | 'update-property-type' | 'blacklist-properties' | 'bulk-pricing-regions' | null;
const supportedBrands: Array<GHAPropertyBrand> = ['luxuryescapes', 'scoopontravel'];

function GHAPropertyManagementContainer() {
  const [openDialog, setOpenDialog] = useState<DialogType>(null);

  const currentBrand = useSelector((state: App.State) => state.tenant?.brand);
  const isSupported = supportedBrands.includes(currentBrand as GHAPropertyBrand);

  const brandAccountTypeOptions = useMemo(
    () => getAccountTypeOptionsForBrand(currentBrand as GHAPropertyBrand),
    [currentBrand],
  );

  const handleSwitchProperties = async (file: File, action: string, accountType?: string) => {
    if (!accountType) {
      throw new Error('Account type is required for bulk switch.');
    }
    await bulkSwitchGHAProperties({
      file,
      action: action as BulkSwitchGHAPropertiesAction,
      accountType: accountType as GHAAccountType,
    });
  };

  const handleUpdatePropertyType = async (file: File, propertyType: string) => {
    await bulkUpdateGHAPropertyType(file, propertyType as GHAPropertyType);
  };

  const handleBlacklistProperties = async (file: File, action: string) => {
    await bulkBlacklistProperties({ file, action: action as BlacklistAction });
  };

  const handleBulkAppendPricingRegions = async (file: File, _action: string, accountType?: string) => {
    if (!accountType) {
      throw new Error('Account type is required for bulk pricing regions.');
    }
    await bulkAppendPricingRegions({ file, accountType: accountType as GHAAccountType });
  };

  return (
    <Container maxWidth={false}>
      <Helmet>
        <title>GHA Property Management</title>
      </Helmet>
      <PageSubheader title="GHA Property Management" />
      {!isSupported ? (
        <Box>
          <Alert severity="info">
            <AlertTitle>GHA Not Available</AlertTitle>
            GHA integration is not available for the currently selected brand. GHA is only available for Luxury Escapes
            and Scoopon.
          </Alert>
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="flex-end">
            <GHABulkOperationsMenu
              onSwitchProperties={() => setOpenDialog('switch-properties')}
              onUpdatePropertyType={() => setOpenDialog('update-property-type')}
              onBlacklistProperties={() => setOpenDialog('blacklist-properties')}
              onBulkPricingRegions={() => setOpenDialog('bulk-pricing-regions')}
            />
          </Box>

          {openDialog === 'switch-properties' && (
            <GHABulkOperationDialog
              open
              onClose={() => setOpenDialog(null)}
              title="Bulk Switch GHA Properties"
              selectLabel="Account Type"
              selectOptions={brandAccountTypeOptions}
              defaultSelectValue={brandAccountTypeOptions[0]?.value}
              radioLabel="Action"
              radioOptions={[
                { value: BulkSwitchGHAPropertiesAction.ENABLE, label: 'Enable on GHA' },
                { value: BulkSwitchGHAPropertiesAction.DISABLE, label: 'Disable on GHA' },
              ]}
              defaultRadioValue={BulkSwitchGHAPropertiesAction.ENABLE}
              submitButtonLabel="Submit"
              successMessage="Bulk operation submitted successfully."
              onSubmit={handleSwitchProperties}
            />
          )}

          {openDialog === 'update-property-type' && (
            <GHABulkOperationDialog
              open
              onClose={() => setOpenDialog(null)}
              title="Bulk Update GHA Property Type"
              radioLabel="GHA Property Type"
              radioOptions={[
                { value: GHAPropertyType.HOTEL, label: 'Hotel' },
                { value: GHAPropertyType.VACATION_RENTAL, label: 'Vacation Rental' },
              ]}
              defaultRadioValue={GHAPropertyType.HOTEL}
              submitButtonLabel="Update GHA Property Type"
              successMessage="Bulk GHA property type update is successful."
              onSubmit={handleUpdatePropertyType}
            />
          )}

          {openDialog === 'blacklist-properties' && (
            <GHABulkOperationDialog
              open
              onClose={() => setOpenDialog(null)}
              title="Bulk Blacklist Properties"
              radioLabel="Action"
              radioOptions={[
                { value: BlacklistAction.BLACKLIST, label: 'Blacklist' },
                { value: BlacklistAction.UNBLACKLIST, label: 'Unblacklist' },
              ]}
              defaultRadioValue={BlacklistAction.BLACKLIST}
              submitButtonLabel="Submit"
              successMessage="Bulk blacklist operation submitted successfully."
              onSubmit={handleBlacklistProperties}
            />
          )}

          {openDialog === 'bulk-pricing-regions' && (
            <GHABulkOperationDialog
              open
              onClose={() => setOpenDialog(null)}
              title="Bulk Update Pricing Regions"
              selectLabel="Account Type"
              selectOptions={brandAccountTypeOptions}
              defaultSelectValue={brandAccountTypeOptions[0]?.value}
              csvHeader="propertyId,regions"
              csvRows={['property-123,US|GB', 'property-456,NZ', 'property-789,US|GB|AE', 'property-012,']}
              csvNotes={
                <>
                  • First row must be the column headers: <strong>propertyId,regions</strong>
                  <br />• Each row contains a property ID and pipe-delimited region codes (e.g., US|GB|NZ)
                  <br />• Region codes must be exactly 2 characters (e.g., US, GB, NZ, AE)
                  <br />• Non-empty regions will be appended to existing regions with automatic deduplication
                  <br />• An empty regions column will clear all pricing regions for that property
                </>
              }
              submitButtonLabel="Update Regions"
              successMessage="Bulk pricing regions update submitted successfully."
              onSubmit={handleBulkAppendPricingRegions}
            />
          )}

          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Property Lists
            </Typography>
            <GHAPropertyListMetadata brand={currentBrand as GHAPropertyBrand} />
          </Box>

          <GHAPropertySearch currentBrand={currentBrand as GHAPropertyBrand} />
        </>
      )}
    </Container>
  );
}

export default GHAPropertyManagementContainer;
